import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"

import { INPUT_HEIGHT_PX } from "@/components/Input/Input.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { CheckmarkMedium, Icon16, StopRegular } from "@/icons/Icons.ts"
import { inputBaseStyles, inputFocusStyles, inputHoverStyles } from "@/styles/inputMixins.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { TaskPreviewPortModel } from "@/routes/tasks/TaskViewScene/TaskPreviewPortModel.ts"

export const TaskPreviewPort = observer((props: {
  model: TaskPreviewPortModel
  disabled: boolean
}) => {
  const port = props.model.previewPort.get()
  const isLoading = props.model.isLoading.get()
  const isPortListened = props.model.isPortBeingListened.get()
  const isWaitingForUpdate = props.model.isWaitingForPreviewUpdate.get()
  const isDisabled = props.disabled || isLoading

  return (
    <PreviewPortContainer isDisabled={isDisabled}>
      <PreviewPortLabel>Port:</PreviewPortLabel>
      <PreviewPortInput
        type="number"
        value={port > 0 ? port.toString() : ""}
        onChange={props.model.handlePreviewPortChange}
        onBlur={props.model.handlePreviewPortBlur}
        placeholder="3000"
        disabled={isDisabled}
      />
      {!isLoading && (
        isWaitingForUpdate ? (
          <Spinner size={16} />
        ) : isPortListened ? (
          <CheckmarkMedium className={Icon16} color="var(--success-color)" />
        ) : (
          <StopRegular className={Icon16} color="var(--error-color)" />
        )
      )}
    </PreviewPortContainer>
  )
})

const PreviewPortContainer = styled.div<{ isDisabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;

  width: 132px;
  height: ${INPUT_HEIGHT_PX}px;
  padding: 0 12px;

  ${inputBaseStyles};
  border-radius: var(--border-radius-24);

  ${textStyles.smallFontSize};

  &:focus-within {
    ${inputFocusStyles};
  }

  &:hover:not(:focus-within) {
    ${inputHoverStyles};
  }

  ${props => props.isDisabled ? `
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  ` : ""}
`

const PreviewPortLabel = styled.span`
  color: var(--secondary-text-color);
  white-space: nowrap;
`

const PreviewPortInput = styled.input`
  flex: 1;
  min-width: 0;
  height: auto;
  padding: 0;
  border: none;
  background: transparent;
  outline: none;

  ${textStyles.smallFontSize};

  &::placeholder {
    color: var(--secondary-text-color);
  }

  /* stylelint-disable property-no-vendor-prefix */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
  /* stylelint-enable property-no-vendor-prefix */
`